import { useEffect, useState } from 'react';
import type { PipelineConfigSettings, PipelineResponse, StageType } from '@orion/contracts';
import { Button, Dialog, Input, Select, Switch, TagInput } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { useSettingsQuery, useUpdateSettings } from '../settings/api.js';
import {
  useAdminPipelinesQuery,
  useCreateLossReason,
  useCreatePipeline,
  useCreateStage,
  useDeletePipeline,
  useDeleteStage,
  useLossReasonsQuery,
  useReorderStages,
  useUpdatePipeline,
  useUpdateStage,
} from './api.js';

const isDesignPipelinePreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'AdminPipeline';

const TYPE_LABEL: Record<StageType, string> = { open: 'Open', won: 'Won', lost: 'Lost' };
const TYPE_OPTIONS: Array<{ value: StageType; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];
const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD — US dollar' },
  { value: 'CAD', label: 'CAD — Canadian dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British pound' },
];

function StageFlow({ pipeline }: { pipeline: PipelineResponse }) {
  const stages = [...pipeline.stages].sort((a, b) => a.position - b.position);
  const openDeals = stages.filter((s) => s.type === 'open').reduce((n, s) => n + s.dealCount, 0);

  return (
    <section aria-labelledby="flow-h" className="flex flex-col gap-3 rounded-dialog border border-border bg-surface px-5 py-4 shadow-card">
      <div className="flex items-baseline justify-between">
        <h2 id="flow-h" className="text-[15px] font-semibold">
          Stage flow
        </h2>
        <span className="text-[13px] text-text-muted">
          {stages.length} stages · {openDeals} open opportunities
        </span>
      </div>
      <ol className="flex list-none items-center gap-1">
        {stages.map((stage, i) => {
          const isCurrent = isDesignPipelinePreview && stage.name === 'Proposal';
          const barColor = stage.type === 'won' ? 'bg-success' : 'bg-primary';
          return (
            <li key={stage.id} className="contents">
              <div
                className={`relative flex h-12 flex-1 flex-col gap-0.5 overflow-hidden rounded-control px-2.5 pt-1.5 ${
                  isCurrent
                    ? 'border-[1.5px] border-primary bg-primary-soft'
                    : stage.type === 'won'
                      ? 'border border-success-soft bg-success-soft/60'
                      : stage.type === 'lost'
                        ? 'border border-danger-soft bg-danger-soft/60'
                        : 'border border-border'
                }`}
              >
                <span className={`text-[13px] font-semibold ${isCurrent ? 'text-primary' : stage.type === 'won' ? 'text-success' : stage.type === 'lost' ? 'text-danger' : ''}`}>
                  {stage.name}
                </span>
                <span className={`text-xs tabular-nums ${isCurrent ? 'text-primary' : stage.type === 'won' ? 'text-success' : stage.type === 'lost' ? 'text-danger' : 'text-text-muted'}`}>
                  {stage.probability}%
                </span>
                {stage.probability > 0 && <span className={`absolute inset-x-0 bottom-0 h-0.75 ${barColor}`} style={{ width: `${stage.probability}%` }} />}
              </div>
              {i < stages.length - 1 && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-text-faint">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function StageEditDialog({ pipelineId, stage, onClose }: { pipelineId: string; stage: PipelineResponse['stages'][number] | null; onClose: () => void }) {
  const update = useUpdateStage();
  const create = useCreateStage();
  const remove = useDeleteStage();
  const [name, setName] = useState(stage?.name ?? '');
  const [probability, setProbability] = useState(stage?.probability ?? 50);
  const [type, setType] = useState<StageType>(stage?.type ?? 'open');
  const [rotAfterDays, setRotAfterDays] = useState<string>(stage?.rotAfterDays?.toString() ?? '');
  const [requiredFields, setRequiredFields] = useState<string[]>(stage?.requiredFields ?? []);
  const saving = update.isPending || create.isPending;

  const save = async () => {
    const input = {
      name,
      probability,
      type,
      rotAfterDays: rotAfterDays.trim() ? Number(rotAfterDays) : undefined,
      requiredFields,
    };
    if (stage) await update.mutateAsync({ pipelineId, stageId: stage.id, input });
    else await create.mutateAsync({ pipelineId, input });
    onClose();
  };

  return (
    <Dialog
      open
      title={stage ? `Edit stage: ${stage.name}` : 'Add stage'}
      onClose={onClose}
      actions={
        <Button size="sm" loading={saving} onClick={save}>
          Save
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="flex gap-3">
          <Input label="Probability (%)" type="number" min={0} max={100} value={probability} onChange={(e) => setProbability(Number(e.target.value))} />
          <Select label="Type" value={type} onChange={(e) => setType(e.target.value as StageType)} options={TYPE_OPTIONS} />
        </div>
        <Input label="Rot after (days)" type="number" min={1} placeholder="No rotting alert" value={rotAfterDays} onChange={(e) => setRotAfterDays(e.target.value)} />
        <TagInput label="Required to enter" value={requiredFields} onChange={setRequiredFields} placeholder="Add a required field…" />
        {stage && (
          <button
            type="button"
            disabled={stage.dealCount > 0}
            title={stage.dealCount > 0 ? `Move ${stage.dealCount} opportunities out first` : undefined}
            onClick={async () => {
              await remove.mutateAsync({ pipelineId, stageId: stage.id });
              onClose();
            }}
            className="self-start text-[13px] font-semibold text-danger disabled:text-text-faint"
          >
            Delete stage
          </button>
        )}
      </div>
    </Dialog>
  );
}

type EditTarget = PipelineResponse['stages'][number] | 'new' | null;

function StagesTable({ pipeline, onEdit, onAdd }: { pipeline: PipelineResponse; onEdit: (stage: PipelineResponse['stages'][number]) => void; onAdd: () => void }) {
  const stages = [...pipeline.stages].sort((a, b) => a.position - b.position);
  const reorder = useReorderStages();
  const updateStage = useUpdateStage();

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= stages.length) return;
    const orderedIds = stages.map((s) => s.id);
    [orderedIds[index], orderedIds[target]] = [orderedIds[target]!, orderedIds[index]!];
    reorder.mutate({ pipelineId: pipeline.id, orderedIds });
  };

  return (
    <section aria-labelledby="stg-h" className="overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
      <div className="flex h-13 items-center justify-between px-5">
        <div className="flex items-baseline gap-2">
          <h2 id="stg-h" className="text-[15px] font-semibold">
            Stages
          </h2>
          <span className="text-[13px] text-text-muted">Drag to reorder</span>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-text-muted">
          <span className="size-2 rounded-full bg-warning" />
          Rotting opportunities are flagged amber
        </span>
      </div>
      <div className="grid h-8 items-center gap-2 px-4 text-xs font-semibold text-text-muted" style={{ gridTemplateColumns: '24px 96px 64px 64px 72px minmax(0,1fr) 44px', background: 'var(--color-surface-subtle)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}>
        <span />
        <span>Stage</span>
        <span>Probability</span>
        <span>Type</span>
        <span>Rotting</span>
        <span>Required to enter</span>
        <span className="text-right">Opportunities</span>
      </div>
      {stages.map((stage, i) => (
        <div key={stage.id} className="grid min-h-13 items-center gap-2 border-b border-divider px-4 py-1.5 text-sm last:border-b-0" style={{ gridTemplateColumns: '24px 96px 64px 64px 72px minmax(0,1fr) 44px' }}>
          <button type="button" onClick={() => move(i, -1)} aria-label={`Move ${stage.name} up`} className="flex text-text-faint hover:text-text-secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden="true">
              <circle cx="9" cy="6" r="0.8" fill="currentColor" />
              <circle cx="15" cy="6" r="0.8" fill="currentColor" />
              <circle cx="9" cy="12" r="0.8" fill="currentColor" />
              <circle cx="15" cy="12" r="0.8" fill="currentColor" />
              <circle cx="9" cy="18" r="0.8" fill="currentColor" />
              <circle cx="15" cy="18" r="0.8" fill="currentColor" />
            </svg>
          </button>
          <button type="button" onClick={() => onEdit(stage)} className="text-left font-semibold hover:text-primary">
            {stage.name}
          </button>
          {stage.type === 'open' ? (
            <label className="flex h-7 w-14 items-center gap-0.5 rounded-control border border-border-strong px-2 text-[13px] focus-within:border-primary focus-within:shadow-focus-ring">
              <input
                type="text"
                inputMode="numeric"
                aria-label={`${stage.name} probability`}
                defaultValue={stage.probability}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n) && n >= 0 && n <= 100 && n !== stage.probability) {
                    updateStage.mutate({ pipelineId: pipeline.id, stageId: stage.id, input: { probability: n } });
                  }
                }}
                className="w-6 tabular-nums outline-none"
              />
              %
            </label>
          ) : (
            <span className="flex h-7 items-center rounded-control border border-border bg-surface-subtle px-2 text-[13px] text-text-muted">{stage.probability}%</span>
          )}
          <span>
            <span
              className={`inline-flex h-5.5 items-center rounded-control px-2 text-xs font-semibold ${
                stage.type === 'won' ? 'bg-success-soft text-success' : stage.type === 'lost' ? 'bg-danger-soft text-danger' : 'border border-border font-medium text-text-secondary'
              }`}
            >
              {TYPE_LABEL[stage.type]}
            </span>
          </span>
          <span className="text-[13px] text-text-secondary">{stage.type === 'open' && stage.rotAfterDays ? `${stage.rotAfterDays} days` : '—'}</span>
          <span className="flex flex-wrap gap-1">
            {stage.requiredFields.length === 0 && <span className="text-[13px] text-text-faint">None</span>}
            {stage.requiredFields.map((f) => (
              <span key={f} className="flex h-5.5 items-center rounded-control border border-border bg-surface-subtle px-2 text-xs font-medium text-text-secondary">
                {f}
              </span>
            ))}
          </span>
          <span className="text-right text-[13px] font-semibold tabular-nums">{stage.dealCount}</span>
        </div>
      ))}
      <div className="flex h-11 items-center justify-between px-3">
        <button type="button" onClick={onAdd} className="flex h-8 items-center gap-1.5 rounded-chip px-2 text-[13px] font-semibold text-primary">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add stage
        </button>
        <span className="text-xs text-text-muted">Move opportunities out before removing a stage.</span>
      </div>
    </section>
  );
}

function DealSettingsCard({ pipeline }: { pipeline: PipelineResponse }) {
  const updatePipeline = useUpdatePipeline();
  const { data: settings, isLoading } = useSettingsQuery<PipelineConfigSettings>('pipeline_config');
  const updateSettings = useUpdateSettings<PipelineConfigSettings>('pipeline_config');
  const { data: lossReasons } = useLossReasonsQuery();
  const createLossReason = useCreateLossReason();
  const [draft, setDraft] = useState<PipelineConfigSettings | null>(null);
  const [newCurrency, setNewCurrency] = useState('');
  const [newReason, setNewReason] = useState('');

  useEffect(() => {
    if (settings && draft === null) setDraft(settings);
  }, [settings, draft]);

  if (isLoading || !draft) return null;
  const save = (patch: Partial<PipelineConfigSettings>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    updateSettings.mutate(next);
  };

  return (
    <section aria-labelledby="ds-h" className="flex flex-col gap-3.5 rounded-dialog border border-border bg-surface p-4.5 shadow-card">
      <h2 id="ds-h" className="text-[15px] font-semibold">
        Opportunity settings
      </h2>

      <Select
        label="Default currency"
        value={pipeline.currency}
        onChange={(e) => updatePipeline.mutate({ id: pipeline.id, input: { currency: e.target.value } })}
        options={CURRENCY_OPTIONS}
      />

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-semibold">Multi-currency</span>
          <Switch checked={draft.multiCurrencyEnabled} onChange={(v) => save({ multiCurrencyEnabled: v })} label="Multi-currency" hideLabel />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {draft.trackedCurrencies.map((c) => (
            <span
              key={c}
              className={`flex h-5.5 items-center gap-1 rounded-control px-2 font-mono text-[11px] ${c === pipeline.currency ? 'bg-primary-soft text-primary' : 'border border-border text-text-secondary'}`}
            >
              {c}
              {c === pipeline.currency && <span className="font-sans">· base</span>}
            </span>
          ))}
          {newCurrency !== '' ? (
            <input
              autoFocus
              value={newCurrency}
              onChange={(e) => setNewCurrency(e.target.value.toUpperCase().slice(0, 3))}
              onBlur={() => {
                if (newCurrency.length === 3 && !draft.trackedCurrencies.includes(newCurrency)) save({ trackedCurrencies: [...draft.trackedCurrencies, newCurrency] });
                setNewCurrency('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              className="h-5.5 w-14 rounded-control border border-border-strong px-1.5 font-mono text-[11px]"
            />
          ) : (
            <button type="button" onClick={() => setNewCurrency(' ')} className="flex h-5.5 items-center rounded-control px-1.5 text-xs font-semibold text-primary">
              + Add
            </button>
          )}
        </div>
        <p className="text-xs text-text-muted">Opportunities may be created in any of these currencies — amounts aren&rsquo;t converted to a base total yet (no exchange-rate feed is wired in).</p>
      </div>

      <div className="h-px bg-divider" />

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] font-semibold">Require loss reason on Lost</span>
          <Switch checked={draft.requireLossReasonOnLost} onChange={(v) => save({ requireLossReasonOnLost: v })} label="Require loss reason on Lost" hideLabel />
        </div>
        <ul aria-label="Loss reasons" className="overflow-hidden rounded-control border border-border">
          {(lossReasons ?? []).map((reason) => (
            <li key={reason.id} className="flex h-8 items-center gap-1.5 border-b border-divider px-2 text-[13px] last:border-b-0">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden="true" className="text-text-faint">
                <circle cx="9" cy="6" r="0.8" fill="currentColor" />
                <circle cx="15" cy="6" r="0.8" fill="currentColor" />
                <circle cx="9" cy="12" r="0.8" fill="currentColor" />
                <circle cx="15" cy="12" r="0.8" fill="currentColor" />
                <circle cx="9" cy="18" r="0.8" fill="currentColor" />
                <circle cx="15" cy="18" r="0.8" fill="currentColor" />
              </svg>
              <span className="flex-1">{reason.name}</span>
              <span className="text-xs text-text-muted">{reason.usageCount}</span>
            </li>
          ))}
          <li className="flex h-8 items-center px-2">
            {newReason !== '' ? (
              <input
                autoFocus
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                onBlur={async () => {
                  const name = newReason.trim();
                  setNewReason('');
                  if (name) await createLossReason.mutateAsync({ name });
                }}
                onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                placeholder="Reason name"
                className="w-full text-[13px] outline-none"
              />
            ) : (
              <button type="button" onClick={() => setNewReason(' ')} className="flex items-center gap-1.5 text-[13px] font-semibold text-primary">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add reason
              </button>
            )}
          </li>
        </ul>
      </div>

      <div className="h-px bg-divider" />

      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[13px] font-semibold">Weighted pipeline</span>
          <span className="text-xs leading-4 text-text-muted">Value × stage probability on dashboards and forecasts.</span>
        </div>
        <Switch checked={draft.weightedPipelineEnabled} onChange={(v) => save({ weightedPipelineEnabled: v })} label="Weighted pipeline" hideLabel />
      </div>
    </section>
  );
}

export function PipelineConfigPage() {
  const { data: pipelines, isLoading } = useAdminPipelinesQuery();
  const createPipeline = useCreatePipeline();
  const deletePipeline = useDeletePipeline();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showNewPipeline, setShowNewPipeline] = useState(false);
  const [newPipelineName, setNewPipelineName] = useState('');
  const [editingStage, setEditingStage] = useState<EditTarget>(null);

  const sorted = [...(pipelines ?? [])].sort((a, b) => a.position - b.position);
  const active = sorted.find((p) => p.id === activeId) ?? sorted[0];
  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'Pipelines & stages' }];

  if (isLoading || !active) return <PageHeader breadcrumb={breadcrumb} />;

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="flex flex-col gap-4 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Pipelines &amp; stages</h1>
            <p className="text-sm leading-5 text-text-muted">Shape how opportunities move from first conversation to close, and what each stage requires.</p>
          </div>
          <button
            type="button"
            onClick={() => setEditingStage('new')}
            className="flex h-9 shrink-0 items-center gap-1.5 rounded-control bg-primary px-3.5 text-sm font-semibold text-white"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add stage
          </button>
        </div>

        <div role="tablist" aria-label="Pipelines" className="flex items-center gap-6 border-b border-border">
          {sorted.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={p.id === active.id}
              onClick={() => setActiveId(p.id)}
              className={`flex h-10 items-center gap-2 border-b-2 text-sm ${p.id === active.id ? 'border-primary font-semibold text-ink' : 'border-transparent font-medium text-text-muted'}`}
            >
              {p.name}
              {p.isDefault && <span className="flex h-4.5 items-center rounded-control bg-surface-subtle px-1.5 text-[11px] font-semibold text-text-secondary">Default</span>}
            </button>
          ))}
          {showNewPipeline ? (
            <input
              autoFocus
              value={newPipelineName}
              onChange={(e) => setNewPipelineName(e.target.value)}
              onBlur={async () => {
                const name = newPipelineName.trim();
                setShowNewPipeline(false);
                setNewPipelineName('');
                if (name) {
                  const created = await createPipeline.mutateAsync({ name, isDefault: false, currency: 'USD' });
                  setActiveId(created.id);
                }
              }}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              placeholder="Pipeline name"
              className="h-8 w-40 rounded-control border border-border-strong px-2 text-sm"
            />
          ) : (
            <button type="button" onClick={() => setShowNewPipeline(true)} className="flex h-10 items-center gap-1.5 text-sm font-semibold text-primary">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              New pipeline
            </button>
          )}
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-[1fr_312px] items-start gap-4 overflow-y-auto">
          <div className="flex flex-col gap-4">
            <StageFlow pipeline={active} />
            <StagesTable pipeline={active} onEdit={setEditingStage} onAdd={() => setEditingStage('new')} />
          </div>
          <DealSettingsCard pipeline={active} />
        </div>
        {!active.isDefault && sorted.length > 1 && (
          <button
            type="button"
            onClick={() => deletePipeline.mutate(active.id)}
            disabled={active.stages.some((s) => s.dealCount > 0)}
            className="self-start text-xs font-semibold text-danger disabled:text-text-faint"
          >
            Delete this pipeline
          </button>
        )}
      </main>
      {editingStage && <StageEditDialog pipelineId={active.id} stage={editingStage === 'new' ? null : editingStage} onClose={() => setEditingStage(null)} />}
    </>
  );
}
